// Commitments & deadlines: dedup, neutral states, and ordering. The table
// is a vigilance tool — it never speculates about whether a commitment was
// met. Three derived states plus the user-marked one:
//   DATE PASSED — the deadline elapsed (no judgment about whether met)
//   PENDING     — deadline in the future
//   OPEN        — no deadline
//   RESOLVED    — the user marked it done (persisted server-side)
//
// Resolutions are keyed by CONTENT (party + normalized text + deadline) so
// they survive re-extraction, which regenerates fact ids. Pure functions —
// unit-tested via node --test.

import type { CaseRecord, RecordFact } from "../types";
import { dedupeFacts, normalizeForDedup, type DedupedFact } from "./dedupeFacts";

export type CommitmentState = "date_passed" | "pending" | "open" | "resolved";

export const COMMITMENT_STATE_LABELS: Record<CommitmentState, string> = {
  date_passed: "Deadline passed",
  pending: "Upcoming",
  open: "No deadline",
  resolved: "Marked done",
};

// Stable identity for a commitment: party + normalized commitment text +
// deadline. The same key drives display dedup AND resolution persistence,
// so resolving a row resolves its duplicates.
export function commitmentKey(f: RecordFact): string {
  return [
    normalizeForDedup(f.party),
    normalizeForDedup(f.summary),
    f.deadline ?? "",
  ].join("|");
}

export interface CommitmentRow {
  fact: RecordFact;
  alsoIn: string[];
  key: string;
  state: CommitmentState;
}

// Dedup: first the display-safe quote dedup (forwarded copies), then the
// content key — two records both stating "Adjuster will contact within 3-5
// business days" for the same party/deadline are ONE obligation.
export function dedupeCommitments(
  facts: RecordFact[],
  recordById: Map<string, CaseRecord>,
): DedupedFact[] {
  const byQuote = dedupeFacts(
    facts.filter(
      (f) => f.fact_type === "commitment" || f.fact_type === "deadline",
    ),
    recordById,
  );
  const seen = new Map<string, DedupedFact>();
  const order: string[] = [];
  for (const d of byQuote) {
    const k = commitmentKey(d.fact);
    const prior = seen.get(k);
    if (!prior) {
      seen.set(k, d);
      order.push(k);
    } else {
      // Merge source records so "Also in N records" stays accurate.
      const ids = new Set([...prior.alsoIn, d.fact.record_id, ...d.alsoIn]);
      ids.delete(prior.fact.record_id);
      prior.alsoIn = [...ids];
    }
  }
  return order.map((k) => seen.get(k)!);
}

// Neutral state. `todayIso` (YYYY-MM-DD) is a parameter for testability.
export function commitmentState(
  deadline: string | null,
  resolved: boolean,
  todayIso: string,
): CommitmentState {
  if (resolved) return "resolved";
  if (!deadline) return "open";
  const d = deadline.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return "open";
  return d < todayIso ? "date_passed" : "pending";
}

// Sort: DATE PASSED first (needs attention), then PENDING by nearest
// deadline, then no-deadline OPEN, then RESOLVED at the bottom.
const STATE_ORDER: Record<CommitmentState, number> = {
  date_passed: 0,
  pending: 1,
  open: 2,
  resolved: 3,
};

export function sortCommitments(rows: CommitmentRow[]): CommitmentRow[] {
  return [...rows].sort((a, b) => {
    const so = STATE_ORDER[a.state] - STATE_ORDER[b.state];
    if (so !== 0) return so;
    if (a.state === "pending") {
      return (a.fact.deadline ?? "") < (b.fact.deadline ?? "") ? -1 : 1;
    }
    return (a.fact.date ?? "") < (b.fact.date ?? "") ? -1 : 1;
  });
}

export function buildCommitmentRows(
  facts: RecordFact[],
  recordById: Map<string, CaseRecord>,
  resolvedKeys: ReadonlySet<string>,
  todayIso: string,
): CommitmentRow[] {
  return sortCommitments(
    dedupeCommitments(facts, recordById).map((d) => {
      const key = commitmentKey(d.fact);
      return {
        fact: d.fact,
        alsoIn: d.alsoIn,
        key,
        state: commitmentState(d.fact.deadline, resolvedKeys.has(key), todayIso),
      };
    }),
  );
}
